import React from 'react';
import { observer } from 'mobx-react';
import { Spin, Tabs } from 'antd';
import { useHistory } from 'react-router-dom';
import style from './style.module.scss';
import { PageTopBar } from '../../components/page-top-bar';
import { SaveButton } from '../../components/action-buttons';
import { ReorderTabLayout } from '../../components/reorder-tab-layout';
import { SortableSection } from '../../components/sortable-section';
import { UnsavedChangesModal } from '../../components/unsaved-changes-modal';
import { CmsUserHistoryBar } from '../../components/cms-user-history-bar';
import { usePlacementOrders } from './hooks/use-placement-orders';
import { useTabGuard } from './hooks/use-tab-guard';
import {
    createPlacementItemRenderer,
    getPlacementHeader,
} from './helpers/placement-helpers';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useSectionPlacementsStore,
} from '../../contexts/mobx';
import { useNavigation } from '../../utils/use-navigation';
import { useSearchParams } from '../../utils/use-search-params';
import type { SectionPlacement } from '../../stores/section-placements';
import { ApplicationTargetEnum } from '../../types/app-selection';
import {
    useUnsavedChanges,
    useCheckNavigation,
} from '../../contexts/unsaved-changes';

type TabKey = 'featured-banners' | 'promotions' | 'other-sections';
const SECTION_PLACEMENT_HISTORY_ACTIONS = [
    'create',
    'delete',
    'sort-order',
] as const;
const FEATURED_PLACEMENT_KEYS = ['featured', 'featured.mealdeal'] as const;
const PROMOTION_PLACEMENT_KEYS = [
    'promotions.coupons',
    'promotions.in_store',
    'promotions.age_restricted',
] as const;
const OTHER_PLACEMENT_KEYS = ['events', 'games_contests'] as const;

const SYNTHETIC_PLACEMENT_KEYS: readonly string[] = ['featured.mealdeal'];
const isSyntheticPlacement = (placement: SectionPlacement) =>
    SYNTHETIC_PLACEMENT_KEYS.includes(placement.key);

const getPlacementsByKeys = (
    placements: SectionPlacement[],
    keys: readonly string[]
) =>
    keys
        .map((key) => placements.find((placement) => placement.key === key))
        .filter((placement): placement is SectionPlacement =>
            Boolean(placement)
        );

export const FeaturedReorderingPage = observer(() => {
    const sectionPlacementsStore = useSectionPlacementsStore();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const navigation = useNavigation();
    const history = useHistory();
    const search = useSearchParams<{ tab?: TabKey }>();
    const appTarget = applicationSelectionStore.appTarget;
    const hasAppTarget = Boolean(appTarget);
    const isQcTarget = appTarget === ApplicationTargetEnum.QC;

    const { hasManagementAccess } = authStore;
    const [historyActionFilter, setHistoryActionFilter] = React.useState<
        string | undefined
    >('sort-order');
    const [activeTab, setActiveTab] = React.useState<TabKey>(() => {
        const tabParam = search.params.tab;
        if (
            tabParam === 'featured-banners' ||
            tabParam === 'promotions' ||
            tabParam === 'other-sections'
        ) {
            return tabParam;
        }
        return 'featured-banners';
    });

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    React.useEffect(() => {
        if (!hasAppTarget) {
            return;
        }
        sectionPlacementsStore.fetchPlacementsHistory(
            1,
            10,
            historyActionFilter
        );
    }, [hasAppTarget, historyActionFilter, sectionPlacementsStore]);

    const allPlacements = sectionPlacementsStore.placements;
    const placements = allPlacements.filter((placement) => placement.isActive);

    const reorderablePlacements = allPlacements.filter(
        (placement) => !isSyntheticPlacement(placement)
    );
    const {
        hasPlacementChanges,
        resetPlacementItems,
        replaceInitialOrders,
        savePlacements,
    } = usePlacementOrders({
        placements: reorderablePlacements,
        isFetching: sectionPlacementsStore.isFetchingPlacements,
        setPlacementItems: (placementId, items) =>
            sectionPlacementsStore.setPlacementItems(placementId, items),
        sortPlacementItems: (placementId, itemIds) =>
            sectionPlacementsStore.sortPlacementItems(placementId, itemIds),
    });

    React.useEffect(() => {
        if (!hasAppTarget) {
            replaceInitialOrders([]);
            return;
        }

        let isCancelled = false;

        // Reset local diff baseline while target changes to avoid stale
        // change detection against the previous target data.
        replaceInitialOrders([]);

        const fetchPlacements = async () => {
            await sectionPlacementsStore.fetchPlacements();
            if (isCancelled) return;
            replaceInitialOrders(
                sectionPlacementsStore.placements.filter(
                    (placement) => !isSyntheticPlacement(placement)
                )
            );
        };

        fetchPlacements();

        return () => {
            isCancelled = true;
        };
    }, [
        hasAppTarget,
        replaceInitialOrders,
        sectionPlacementsStore,
        applicationSelectionStore.selectedApplication,
    ]);
    const { checkNavigation, setSelectedMenuKey } = useCheckNavigation();

    React.useEffect(() => {
        if (hasAppTarget) {
            return;
        }
        setSelectedMenuKey('');
        navigation.goToHome();
    }, [hasAppTarget, navigation, setSelectedMenuKey]);

    const featuredPlacements = getPlacementsByKeys(
        placements,
        FEATURED_PLACEMENT_KEYS
    );
    const promotionPlacementKeys = isQcTarget
        ? PROMOTION_PLACEMENT_KEYS.filter(
              (key) => key !== 'promotions.age_restricted'
          )
        : PROMOTION_PLACEMENT_KEYS;
    const promotionsPlacements = getPlacementsByKeys(
        placements,
        promotionPlacementKeys
    );
    const otherPlacements = getPlacementsByKeys(
        placements,
        OTHER_PLACEMENT_KEYS
    );

    const activePlacements = (() => {
        switch (activeTab) {
            case 'featured-banners':
                return featuredPlacements;
            case 'promotions':
                return promotionsPlacements;
            case 'other-sections':
                return otherPlacements;
            default:
                return [];
        }
    })();

    const hasChanges = placements.some(hasPlacementChanges);

    const saveState = {
        loading: activePlacements.some((placement) =>
            sectionPlacementsStore.isSortingPlacement(placement.id)
        ),
        disabled:
            activePlacements.length === 0 ||
            !activePlacements.some(hasPlacementChanges),
    };

    const handleSave = async () => {
        await savePlacements(activePlacements.filter(hasPlacementChanges));
    };

    const { clearDirty } = useUnsavedChanges({
        dirty: hasChanges,
        onSave: async () => {
            await savePlacements(placements.filter(hasPlacementChanges));
        },
    });

    const isLoading = sectionPlacementsStore.isFetchingPlacements;
    const commitTab = React.useCallback(
        (nextTab: TabKey) => {
            setActiveTab(nextTab);
            search.setParam('tab', nextTab);
        },
        [search]
    );

    const handleTabSaveAll = React.useCallback(async () => {
        await savePlacements(placements.filter(hasPlacementChanges));
        clearDirty();
    }, [clearDirty, hasPlacementChanges, placements, savePlacements]);

    const handleTabDiscardAll = React.useCallback(() => {
        resetPlacementItems();
        clearDirty();
    }, [clearDirty, resetPlacementItems]);

    const {
        isModalOpen: showTabModal,
        isSaving: isTabSaving,
        handleTabChange,
        handleModalClose: handleTabModalClose,
        handleDiscard: handleTabDiscard,
        handleSave: handleTabSave,
    } = useTabGuard({
        activeTab,
        hasChanges,
        onSave: handleTabSaveAll,
        onDiscard: handleTabDiscardAll,
        onCommitTab: commitTab,
    });

    const placementHeaderActions = React.useMemo(
        () => ({
            featured: () => navigation.goToFeaturedList(),
            coupons: () => navigation.goToCoupons(),
            inStore: () => navigation.goToCouponsInStore(),
            events: () => navigation.goToEvents(),
            games: () => navigation.goToGames(),
            fallback: () => navigation.goToHome(),
        }),
        [navigation]
    );
    const renderPlacementItem = React.useCallback(
        (placement: SectionPlacement) =>
            createPlacementItemRenderer(
                { id: placement.id, key: placement.key },
                {
                    onEditFeatured: (contentId) =>
                        checkNavigation(() =>
                            navigation.goToFeaturedEdit(contentId)
                        ),
                    onEditPromotionCoupon: (contentId) =>
                        checkNavigation(() =>
                            navigation.goToCouponEdit(contentId)
                        ),
                    onEditPromotionInStore: (contentId) =>
                        checkNavigation(() =>
                            navigation.goToInStoreCouponEdit(contentId)
                        ),
                    onEditPromotionFallback: () =>
                        checkNavigation(() => navigation.goToCoupons()),
                    onEditEvent: (contentId) =>
                        checkNavigation(() =>
                            navigation.goToEventEdit(contentId)
                        ),
                    onEditExternalGame: (contentId) =>
                        checkNavigation(() =>
                            navigation.goToGameEdit(contentId, 'external')
                        ),
                    onEditNhlGame: (contentId) =>
                        checkNavigation(() =>
                            navigation.goToNhlGameEdit(contentId)
                        ),
                    onEditLabattGame: (contentId) =>
                        checkNavigation(() =>
                            navigation.goToGameEdit(contentId, 'external')
                        ),
                }
            ),
        [checkNavigation, navigation]
    );

    const getPlacementCreateTarget = React.useCallback(
        (placement: SectionPlacement) => {
            switch (placement.key) {
                case 'featured':
                    return { path: '/featured/create' };
                case 'promotions.coupons':
                    return { path: '/coupon/create' };
                case 'promotions.in_store':
                    return { path: '/in-store-promotion/create' };
                case 'events':
                    return { path: '/event/create' };
                case 'games_contests':
                    return { path: '/game-external/create' };
                default:
                    return null;
            }
        },
        []
    );

    const handleAddPlacementItem = React.useCallback(
        (placement: SectionPlacement) => {
            const target = getPlacementCreateTarget(placement);
            if (!target) return;

            const params = new URLSearchParams({
                returnTo: `/featured-reordering?tab=${activeTab}`,
            });

            checkNavigation(() =>
                history.push(`${target.path}?${params.toString()}`)
            );
        },
        [activeTab, checkNavigation, getPlacementCreateTarget, history]
    );
    const renderPlacementSections = React.useCallback(
        (
            tabPlacements: SectionPlacement[],
            tabEmptyState: string,
            sectionEmptyState?: React.ReactNode
        ) => {
            if (tabPlacements.length === 0) {
                return <div className={style.empty_state}>{tabEmptyState}</div>;
            }

            return tabPlacements.map((placement) => {
                const header = getPlacementHeader(
                    placement,
                    placementHeaderActions
                );
                const onViewAll = header.onViewAll;
                const createTarget = getPlacementCreateTarget(placement);
                const isMealDeal = placement.key === 'featured.mealdeal';
                const synthetic = isSyntheticPlacement(placement);
                const placementEmptyState = isMealDeal
                    ? 'There are no active meal deals currently.'
                    : sectionEmptyState;

                return (
                    <div
                        key={`${placement.key}-${placement.id}`}
                        className={style.age_restricted_column}
                    >
                        <SortableSection
                            title={header.title}
                            viewAllLabel={header.viewAllLabel}
                            onViewAll={
                                onViewAll
                                    ? () => checkNavigation(onViewAll)
                                    : undefined
                            }
                            items={placement.items}
                            setItems={(newState) =>
                                sectionPlacementsStore.setPlacementItems(
                                    placement.id,
                                    newState
                                )
                            }
                            renderItem={renderPlacementItem(placement)}
                            onAdd={
                                synthetic || !createTarget
                                    ? undefined
                                    : () => handleAddPlacementItem(placement)
                            }
                            emptyState={placementEmptyState}
                            disabled={synthetic}
                        />
                        {placement.key === 'promotions.age_restricted' && (
                            <p className={style.age_restricted_info}>
                                To set up an age-restricted product, create a
                                Coupon or In-Store Product and assign it an age
                                restricted category (cigarettes, vaping, beer,
                                wine, lottery).
                            </p>
                        )}
                        {isMealDeal && (
                            <p className={style.age_restricted_info}>
                                Only 1 active featured banner can be active at
                                once.
                            </p>
                        )}
                    </div>
                );
            });
        },
        [
            checkNavigation,
            getPlacementCreateTarget,
            handleAddPlacementItem,
            placementHeaderActions,
            renderPlacementItem,
            sectionPlacementsStore,
        ]
    );
    const fetchPlacementsHistoryCallback = React.useCallback(
        async (page: number, perPage: number, action?: string) => {
            await sectionPlacementsStore.fetchPlacementsHistory(
                page + 1,
                perPage,
                action
            );
        },
        [sectionPlacementsStore]
    );
    const historyBar = (
        <CmsUserHistoryBar
            cmsUserHistory={sectionPlacementsStore.placementsHistory?.results}
            fetchHistory={fetchPlacementsHistoryCallback}
            historyData={sectionPlacementsStore.placementsHistory}
            isFetching={sectionPlacementsStore.isFetchingPlacementsHistory}
            actionFilter={historyActionFilter}
            onActionFilterChange={(action) => setHistoryActionFilter(action)}
            availableActions={SECTION_PLACEMENT_HISTORY_ACTIONS}
        />
    );

    React.useEffect(() => {
        const tabParam = search.params.tab;
        if (
            tabParam &&
            (tabParam === 'featured-banners' ||
                tabParam === 'promotions' ||
                tabParam === 'other-sections') &&
            tabParam !== activeTab
        ) {
            setActiveTab(tabParam);
        }
    }, [activeTab, search.params.tab]);

    if (!hasAppTarget) {
        return null;
    }

    return (
        <div className={style.page}>
            <PageTopBar title="Featured & Reordering">
                <SaveButton
                    loading={saveState.loading}
                    disabled={saveState.disabled || saveState.loading}
                    onClick={handleSave}
                />
            </PageTopBar>

            <div className={`${style.content} content_with_margins`}>
                <Tabs
                    className={style.tabs}
                    activeKey={activeTab}
                    onChange={handleTabChange}
                >
                    <Tabs.TabPane tab="Featured Banners" key="featured-banners">
                        {isLoading ? (
                            <Spin />
                        ) : (
                            <ReorderTabLayout history={historyBar}>
                                {renderPlacementSections(
                                    featuredPlacements,
                                    'No featured placement configured'
                                )}
                            </ReorderTabLayout>
                        )}
                    </Tabs.TabPane>

                    <Tabs.TabPane tab="Promotions" key="promotions">
                        {isLoading ? (
                            <Spin />
                        ) : (
                            <ReorderTabLayout
                                history={historyBar}
                                columns={isQcTarget ? 2 : 3}
                            >
                                {renderPlacementSections(
                                    promotionsPlacements,
                                    'No promotions configured',
                                    'No promotions'
                                )}
                            </ReorderTabLayout>
                        )}
                    </Tabs.TabPane>

                    <Tabs.TabPane tab="Other sections" key="other-sections">
                        {isLoading ? (
                            <Spin />
                        ) : (
                            <ReorderTabLayout history={historyBar}>
                                {renderPlacementSections(
                                    otherPlacements,
                                    'No placement to show',
                                    'No items'
                                )}
                            </ReorderTabLayout>
                        )}
                    </Tabs.TabPane>
                </Tabs>
            </div>
            {showTabModal && (
                <UnsavedChangesModal
                    onSave={handleTabSave}
                    onDiscard={handleTabDiscard}
                    onClose={handleTabModalClose}
                    isSaving={isTabSaving}
                    saveDisabled={false}
                />
            )}
        </div>
    );
});
